/* eslint-disable react-hooks/exhaustive-deps */
// React Imports
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

// Component Imports
import Loader from "../../../components/Loader/Index";
import GeoMapLeaflet from "../../../components/_utils/GeoMapLeaflet";
import SearchBar from "../../../components/_utils/CommonSearchComponents/SearchBar";
import CommonContainerCard from "../../../components/_utils/CommonContainerCard";
import TopNavSmallButton from "../../../components/_utils/CommonButtons/TopNavSmallButton";
import TitleSubtitlePair from "../../../components/_utils/TitleSubtitlePair";
import SwitcherTabs from "../../../components/_utils/CommonButtons/SwitcherTabs";
import CommonDetailCard from "../../../components/_utils/CommonDetailCard";
import { formatDate } from "../../../components/ScheduleTransactions/helpers";
import CommonToast from "../../../components/_utils/CommonToast";
import PrimaryButton from "../../../components/_utils/CommonButtons/PrimaryButton";
import CINVerification from "../CINVerification";
import LargePopupRedirect from "../../../components/_utils/CommonPopups/CommonLargePopupRedirect";
import useServiceChecker from "../../../components/ServiceChecker";
import TopNavComponent from "../../../components/_utils/CommonTopNav/TopNavComponent";

// Utils Imports
import formatDateViewAll from "../../../utils/formatDateViewAll";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../utils/configurationProvider";
import TEXT from "../../../../src/utils/globalText";
import Validation from "../../../utils/validation";

import { toast } from "react-toastify";
import { countryDemonyms } from "./countryCodes";
import ICONS from "../../../assets/Icons";
import {
  getDINDetails,
  verifyDIN,
  resetSelectedDin,
  resetGetDinData,
} from "../../../redux/features/sharedResources/sharedResources";
import useRestrictedAction from "../../../hooks/useRestrictedAction";

const DINVerification = ({ din_no, popup }) => {
  const inputRef = useRef("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [ensureAny] = useServiceChecker();
  const dinNumber = location.state?.dinNumber;
  const initialDinFromLocation = useRef(
    location.state?.dinNumber?.toUpperCase() || "",
  );

  const [activeButton, setActiveButton] = useState(
    GlobalConfiguration?.lengthCheckZero,
  );
  const [loading, setLoading] = useState(true);
  const [dinNo, setDinNo] = useState("");
  const [prevDinNo, setPrevDinNo] = useState("");
  const [tabIndex, setTabIndex] = useState(
    activeButton === GlobalConfiguration?.lengthCheckZero
      ? GlobalConfiguration?.lengthCheckZero
      : activeButton === GlobalConfiguration?.lengthCheckOne
        ? GlobalConfiguration?.lengthCheckOne
        : activeButton === GlobalConfiguration?.lengthCheckTwo
          ? GlobalConfiguration?.lengthCheckTwo
          : GlobalConfiguration?.lengthCheckZero,
  );
  const [prevIndex, setPrevIndex] = useState(
    GlobalConfiguration?.lengthCheckZero,
  );
  const [animationClass, setAnimationClass] = useState("");
  const [dinStatus, setDinStatus] = useState(null);
  const [cinNo, setCinNo] = useState("");
  const [showCINPopup, setCINShowPopup] = useState(false);

  const {
    dinVerificationData,
    selectedDinData,
    dinDetailsData,
    dinDetailsLoading,
    dinDetailsError,
    dinVerificationLoading,
  } = useSelector((state) => state.sharedResources);

  const services = useSelector((state) => state.refreshToken.services);

  const handleDin = (e) => {
    const value = e.target ? e.target.value : e;
    if (!/^\d*$/.test(value)) return;
    if (!value.length) {
      setActiveButton(GlobalConfiguration?.lengthCheckZero);
      setTabIndex(GlobalConfiguration?.lengthCheckZero);
      setPrevIndex(GlobalConfiguration?.lengthCheckZero);
    }
    if (value.length > GlobalConfiguration.dinLength) {
      return;
    }
    if (!Validation.alphanumericRegex?.test(value)) {
      return;
    }
    setDinNo(value.toUpperCase());
    setPrevDinNo("");
    setDinStatus(null);
  };

  const validateDin = (din, { showToast = true } = {}) => {
    const isValid =
      Validation.dinRegex?.test(din) &&
      din.length === GlobalConfiguration.dinLength;

    if (!isValid) {
      setDinStatus("error");

      if (showToast) {
        CommonToast({
          text: "Invalid DIN!",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
        });
      }
      return false;
    }

    setDinStatus("success");
    return true;
  };

  const handleDinClick = async (showToast = false) => {
    setActiveButton(GlobalConfiguration?.lengthCheckZero);
    setTabIndex(GlobalConfiguration?.lengthCheckZero);

    if (!validateDin(dinNo)) {
      return;
    }

    setPrevDinNo(dinNo);
    if (showToast) {
      toast.dismiss();
      CommonToast({
        text: "Updating details.",
        icon: ICONS?.LOADER_GIF,
        variant: "var(--processing)",
        buttonNeeded: false,
        btnProps: {
          enabledBgColor: "var(--primary-bg)",
          enabledTextColor: "var(--text-primary)",
          btnType: "button",
          isDisabled: false,
          hoverOption: "white",
        },
      });
    }

    if (!showToast) {
      await dispatch(getDINDetails({ search_string: dinNo }));
    } else {
      const response = await dispatch(
        verifyDIN({ search_string: dinNo, showToast }),
      );

      if (response?.payload?.status === true) {
        toast.dismiss();
        CommonToast({
          text: "Details updated successfully.",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
          variant: "var(--success)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
      } else {
        toast.dismiss();
        CommonToast({
          text: "Failed to update.",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
      }
    }
    // }
    setActiveButton(GlobalConfiguration?.lengthCheckZero);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !isButtonDisabled) {
      handleDinClick(false);
    }
  };

  const tabOptions = [
    {
      label: "Details",
      key: "details",
      handler: () => setActiveButton(GlobalConfiguration?.lengthCheckZero),
    },
    {
      label: "Corporate Profile",
      key: "corporate",
      handler: () => setActiveButton(GlobalConfiguration?.lengthCheckOne),
    },
    {
      label: "Address",
      key: "address",
      handler: () => setActiveButton(GlobalConfiguration?.lengthCheckTwo),
    },
  ];

  const moreInfoEntries = [
    {
      heading: "Email ID",
      content:
        dinVerificationData?.din_details?.din_number ===
        dinDetailsData?.din_details?.din_number
          ? dinVerificationData?.din_details?.email
          : dinDetailsData?.din_details?.email
            ? dinDetailsData?.din_details?.email
            : "N/A",
      type: "text",
    },
    {
      heading: "Father name",
      content: (() => {
        const fatherName =
          dinVerificationData?.din_details?.din_number ===
          dinDetailsData?.din_details?.din_number
            ? dinVerificationData?.din_details?.father_name
            : dinDetailsData?.din_details?.father_name
              ? dinDetailsData?.din_details?.father_name
              : "N/A";

        if (!fatherName) return "N/A";
        return fatherName
          .toLowerCase()
          .split(" ")
          .map(
            (word) =>
              word.charAt(GlobalConfiguration?.lengthCheckZero).toUpperCase() +
              word.slice(GlobalConfiguration?.lengthCheckOne),
          )
          .join(" ");
      })(),
      type: "text",
    },
    {
      heading: "Date of birth",
      content: (() => {
        const dobValue =
          dinVerificationData?.din_details?.din_number ===
          dinDetailsData?.din_details?.din_number
            ? dinVerificationData?.din_details?.dob ||
              dinDetailsData?.din_details?.dob
            : dinDetailsData?.din_details?.dob;
        return dobValue ? formatDate(dobValue) : "N/A";
      })(),
      type: "date",
    },
  ];

  const originDetailsEntries = [
    {
      heading: "Created on",
      content: (() => {
        const createdOn =
          dinVerificationData?.din_details?.din_number ===
          dinDetailsData?.din_details?.din_number
            ? dinVerificationData?.din_details?.created_on ||
              dinDetailsData?.din_details?.created_on
            : dinDetailsData?.din_details?.created_on;
        return createdOn ? formatDateViewAll(createdOn, false) : "Not given";
      })(),
      type: "text",
    },
    {
      heading: "Updated on",
      content: (() => {
        const createdOn =
          dinVerificationData?.din_details?.din_number ===
          dinDetailsData?.din_details?.din_number
            ? dinVerificationData?.din_details?.last_updated_on ||
              dinDetailsData?.din_details?.last_updated_on
            : dinDetailsData?.din_details?.last_updated_on;
        return createdOn ? formatDateViewAll(createdOn, false) : "Not given";
      })(),
      type: "text",
    },
  ];

  const isDinLengthValid = dinNo.length === GlobalConfiguration.dinLength;
  const isDinChanged = dinNo !== prevDinNo;
  const hasLoading = !!dinDetailsLoading;
  const hasError = !!dinDetailsError && !isDinChanged;

  const isButtonDisabled =
    !dinNo ||
    !isDinLengthValid ||
    hasLoading ||
    hasError ||
    dinStatus === "error" ||
    (!isDinChanged && dinStatus === "success") ||
    (dinDetailsData?.status &&
      dinDetailsData?.din_details?.din_number === dinNo);

  const { showRestrictedAction, RestrictedActionPopup } = useRestrictedAction();

  const actionRestricted = () => {
    showRestrictedAction({
      path: "/settings/DIN_verification",
      state: {
        dinNumber: dinNo,
      },
    });
  };

  useLayoutEffect(() => {
    if (tabIndex !== prevIndex) {
      const direction =
        tabIndex > prevIndex ? "animate-slide-next" : "animate-slide-prev";

      setAnimationClass(direction);
      setTimeout(() => {
        setPrevIndex(tabIndex);
      }, IntervalConfiguration?.intervalSemiMid);
    }
  }, [tabIndex]);

  useEffect(() => {
    if (dinDetailsData?.din_details?.present_address) {
      setLoading(false);
    }
  }, [dinDetailsData]);

  useEffect(() => {
    if (services && !services?.includes("VDIN")) {
      navigate("/feature_not_assigned");
    }
    if (selectedDinData) {
      setDinNo(selectedDinData);
      setPrevDinNo(selectedDinData);
      setDinStatus("success");
    } else if (dinNumber) {
      setDinNo(dinNumber);
      setPrevDinNo(dinNumber);
      setDinStatus("success");
    } else {
      setDinNo("");
      setPrevDinNo("");
      setDinStatus(null);
    }
    return () => {
      dispatch(resetSelectedDin());
      dispatch(resetGetDinData());
    };
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [dinNo, isButtonDisabled]);

  useEffect(() => {
    if ((popup && din_no) || dinNumber) {
      setDinNo(din_no?.toUpperCase() || dinNumber?.toUpperCase());
      setPrevDinNo("");
      dispatch(resetGetDinData());
      dispatch(resetSelectedDin());
    }
  }, [popup, din_no, dinNumber]);

  useEffect(() => {
    const isValidDin =
      dinNo &&
      dinNo.length === GlobalConfiguration.dinLength &&
      dinNo !== prevDinNo;

    // popup trigger
    if (popup && isValidDin) {
      handleDinClick(false);
      return;
    }

    // location one-time trigger
    if (
      initialDinFromLocation.current &&
      dinNo === initialDinFromLocation.current &&
      isValidDin
    ) {
      handleDinClick(false);
      initialDinFromLocation.current = "";
    }
  }, [popup, dinNo, prevDinNo]);

  return (
    <div
      className={`flex flex-col ${popup ? "h-height-full" : "h-screen"} bg-primary-bg overflow-y-auto overflow-x-hidden Qmd:overflow-x-hidden`}
    >
      <>{RestrictedActionPopup}</>
      {showCINPopup && (
        <div>
          <LargePopupRedirect
            headerTitle="CIN Details"
            isfullViewrequired={true}
            fullviewhandleFunction={() =>
              navigate(`/settings/CIN_verification`, {
                state: { cinNumber: cinNo },
              })
            }
            closeiconhandleFunction={() => setCINShowPopup(false)}
          >
            <CINVerification
              cin_no={cinNo}
              popup={true}
            />
          </LargePopupRedirect>
        </div>
      )}
      <div
        className={`Qsmd:hidden ${popup || showCINPopup ? "hidden" : "bg-primary-nav shadow-box-shadow-bottom fixed top-top-base left-left-base z-z-index-tooltip w-width-full block"}`}
      >
        <TopNavComponent
          isFormattingNotRequired={true}
          topNavHeader="DIN Verification"
        />
      </div>
      <div
        className={`${popup ? "hidden" : "mt-margin-xl Qsmd:mt-margin-base Qsmd:min-h-navbar-breadcrumb-height bg-primary-nav"}`}
      ></div>
      <div
        className={`${popup ? "hidden" : "p-mobile-padding-l Qmd:p-padding-md w-width-full bg-primary-nav opacity-base-opacity flex flex-col justify-center items-center z-z-index-header shadow-box-shadow-bottom"}`}
        style={{
          paddingTop:
            activeButton === GlobalConfiguration?.lengthCheckOne
              ? "var(--padding-md)"
              : undefined,
          paddingBottom:
            activeButton === GlobalConfiguration?.lengthCheckOne
              ? "var(--padding-md)"
              : undefined,
        }}
      >
        <SearchBar
          searchText={dinNo}
          searchRef={inputRef}
          placeholder="Search DIN number"
          isSearchDisabled={popup ? true : false}
          onInput={handleDin}
          btnText="Fetch"
          onClick={() => {
            if (
              (isDinLengthValid && dinDetailsLoading === null) ||
              isDinChanged
            ) {
              handleDinClick(false);
              setActiveButton(GlobalConfiguration?.lengthCheckZero);
              setTabIndex(GlobalConfiguration?.lengthCheckZero);
              setPrevIndex(GlobalConfiguration?.lengthCheckZero);
            }
          }}
          isBtnDisable={isButtonDisabled}
          txtColor={
            isButtonDisabled ? "var(--text-primary)" : "var(--primary-bg)"
          }
          btnColor={
            isButtonDisabled ? "var(--text-disabled)" : "var(--text-primary)"
          }
          inActiveVariantNeeded={true}
        />
      </div>
      {dinDetailsLoading ? (
        <div className="w-full  flex items-center justify-center">
          <div className="owner_loader-container">
            <Loader />
          </div>
        </div>
      ) : !dinDetailsLoading && dinDetailsData?.status ? (
        <>
          <div className="w-auto bg-primary-nav">
            <div className="mx-auto sm:mt-margin-base max-w-max-content-width">
              <div className="flex justify-between items-center border-0 md:border-b border-primary-bg md:py-padding-base p-mobile-padding-l Qmd:px-padding-md Qlg:px-padding-base">
                <div className="flex mx-margin-base md:ml-margin-sm md:mr-margin-lg gap-gap-sm">
                  <TitleSubtitlePair
                    title={(() => {
                      const userName =
                        dinVerificationData?.din_details?.din_number ===
                        dinDetailsData?.din_details?.din_number
                          ? dinVerificationData?.din_details?.full_name ||
                            dinDetailsData?.din_details?.full_name
                          : dinDetailsData?.din_details?.full_name;

                      if (!userName) return "N/A";
                      return userName
                        .toLowerCase()
                        .split(" ")
                        .map(
                          (word) =>
                            word
                              .charAt(GlobalConfiguration?.lengthCheckZero)
                              .toUpperCase() +
                            word.slice(GlobalConfiguration?.lengthCheckOne),
                        )
                        .join(" ");
                    })()}
                    subtitle="Username"
                    titleColor="text-primary"
                    variant="md"
                  />
                </div>

                <div className="hidden md:flex justify-end flex-col md:flex-row items-center">
                  <TopNavSmallButton
                    btnText={
                      <>
                        Refresh <br /> details
                      </>
                    }
                    btnType="button"
                    handleFunction={() => {
                      if (popup) {
                        actionRestricted();
                        return;
                      }
                      handleDinClick(true);
                    }}
                    enabledTextColor="var(--text-primary)"
                    enabledBgColor="var(--primary-nav-act)"
                    disabledBgColor="var(--primary-nav-act)"
                    icon={ICONS?.REFRESH_BALANCE}
                    hoverOption="white"
                    fullWidthRequired={false}
                    isDisabled={popup || dinVerificationLoading}
                  />
                </div>
              </div>
              <div className="mx-mobile-margin-l block md:hidden border-b border-solid border-primary-bg"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 items-center py-padding-base md:py-padding-md">
                <div className="border-0 md:border-r border-primary-bg md:py-padding-base p-mobile-padding-l Qmd:px-padding-md Qlg:px-padding-base">
                  <div className="flex md:ml-margin-sm mx-margin-base">
                    <TitleSubtitlePair
                      title={dinDetailsData?.din_details?.din_number}
                      subtitle="DIN number"
                      titleColor="text-primary"
                      variant="md"
                    />
                  </div>
                </div>
                <div className="mx-mobile-margin-l block md:hidden border-b border-solid border-primary-bg"></div>
                <div
                  className={`flex md:py-padding-base md:px-padding-md ${!dinDetailsData?.din_details?.pan_number ? "p-mobile-padding-l md:pl-padding-md" : "p-padding-button-s"} md:pr-padding-md`}
                >
                  <TitleSubtitlePair
                    title={dinDetailsData?.din_details?.pan_number || "N/A"}
                    showArrow={dinDetailsData?.din_details?.pan_number && true}
                    onClick={() => {
                      if (dinDetailsData?.din_details?.pan_number) {
                        navigate("/settings/PAN_verification", {
                          state: {
                            panNumber: dinDetailsData?.din_details?.pan_number,
                          },
                        });
                      }
                    }}
                    hoverOption="white"
                    subtitle="PAN number"
                    titleColor="text-primary"
                    variant="md"
                  />
                </div>
                <div className="mx-mobile-margin-l block md:hidden border-b border-solid border-primary-bg"></div>
                <div className="md:border-b-0 md:border-l border-primary-bg md:py-padding-base p-mobile-padding-l Qmd:px-padding-md">
                  <TitleSubtitlePair
                    title={(() => {
                      const nationality =
                        dinVerificationData?.din_details?.din_number ===
                        dinDetailsData?.din_details?.din_number
                          ? dinVerificationData?.din_details?.nationality ||
                            dinDetailsData?.din_details?.nationality
                          : dinDetailsData?.din_details?.nationality;

                      return countryDemonyms[nationality] || "N/A";
                    })()}
                    subtitle="Nationality"
                    titleColor="text-primary"
                    variant="md"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-primary-nav-act block md:hidden mt-margin-md">
            <p className="bg-primary-card px-mobile-padding-l py-padding-sm text-sm font-semibold text-text-primary flex items-center">
              Quick Actions
            </p>
            <div className="flex md:hidden justify-end flex-col md:flex-row items-center h-[var(--topnav-small-btn-width)]">
              <TopNavSmallButton
                btnText="Refresh details"
                btnType="button"
                handleFunction={() => {
                  if (popup) {
                    actionRestricted();
                    return;
                  }
                  handleDinClick(true);
                }}
                enabledTextColor="var(--text-primary)"
                enabledBgColor="var(--primary-nav-act)"
                disabledBgColor="var(--primary-nav-act)"
                icon={ICONS?.REFRESH_BALANCE}
                hoverOption="white"
                fullWidthRequired
                isDisabled={popup || dinVerificationLoading}
              />
            </div>
          </div>
          <div className="flex justify-center px-padding-base py-padding-md w-width-full">
            <div className="flex flex-col gap-gap-md max-w-max-content-width w-width-full">
              <div className="pl-mobile-padding-l Qmd:pl-padding-md Qlg:pl-padding-base">
                <SwitcherTabs
                  tabs={tabOptions}
                  initialActiveTab={
                    activeButton === GlobalConfiguration?.lengthCheckZero
                      ? "details"
                      : activeButton === GlobalConfiguration?.lengthCheckOne
                        ? "corporate"
                        : "address"
                  }
                  setIndex={setTabIndex}
                  type="single"
                />
              </div>
              <div className="relative">
                {tabIndex === GlobalConfiguration?.lengthCheckZero && (
                  <div className={tabIndex === prevIndex ? "" : animationClass}>
                    <div className="flex gap-gap-md flex-col sm:flex-col md:flex-row lg:flex-row items-center md:items-start lg:items-start px-mobile-padding-l Qmd:px-padding-md Qlg:px-padding-base">
                      <CommonDetailCard
                        cardTitle="More Info"
                        numCols={GlobalConfiguration?.lengthCheckOne}
                        variant="third"
                        entries={moreInfoEntries}
                      />

                      <CommonDetailCard
                        cardTitle="Origin Details"
                        numCols={GlobalConfiguration?.lengthCheckOne}
                        variant="third"
                        entries={originDetailsEntries}
                      />
                    </div>
                  </div>
                )}

                {tabIndex === GlobalConfiguration?.lengthCheckOne && (
                  <div className={animationClass}>
                    <div className="flex flex-wrap justify-start gap-gap-md px-mobile-padding-l Qmd:px-padding-md Qlg:px-padding-base">
                      {(() => {
                        const cinDetails =
                          dinVerificationData?.din_details?.din_number ===
                          dinDetailsData?.din_details?.din_number
                            ? dinVerificationData?.din_details?.cin_details ||
                              dinDetailsData?.din_details?.cin_details
                            : dinDetailsData?.din_details?.cin_details;
                        if (
                          !cinDetails ||
                          cinDetails.length ===
                            GlobalConfiguration?.lengthCheckZero
                        ) {
                          return (
                            <div className="flex flex-col bg-primary-card w-width-full p-padding-md gap-y-gap-md rounded-border-radius-md">
                              <p className="text-font-sm text-text-primary font-semibold">
                                CIN details
                              </p>
                              <p className="text-font-sm text-text-primary font-normal">
                                CIN details not available.
                              </p>
                            </div>
                          );
                        }

                        return cinDetails.map((data, i) => (
                          <CommonDetailCard
                            key={data?.cin_number || i}
                            cardTitle={data?.company_name
                              ?.toLowerCase()
                              ?.split(" ")
                              ?.map(
                                (word) =>
                                  word
                                    .charAt(
                                      GlobalConfiguration?.lengthCheckZero,
                                    )
                                    .toUpperCase() +
                                  word.slice(
                                    GlobalConfiguration?.lengthCheckOne,
                                  ),
                              )
                              ?.join(" ")}
                            numCols={GlobalConfiguration?.lengthCheckOne}
                            variant="fourth"
                            isDisabled={popup}
                            entries={[
                              {
                                heading: "CIN number",
                                content: data?.cin_number,
                                type: "text",
                                isDisabled: popup,
                              },
                            ]}
                            onClick={() => {
                              if (popup) {
                                actionRestricted();
                                return;
                              }
                              if (ensureAny(["VCIN"]).status) {
                                setCINShowPopup(true);
                                setCinNo(data?.cin_number);
                              } else {
                                navigate("/feature_not_assigned", {
                                  state: {
                                    unavailableService: "VCIN",
                                  },
                                });
                              }
                            }}
                          />
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {tabIndex === GlobalConfiguration?.lengthCheckTwo && (
                  <div className={animationClass}>
                    <CommonContainerCard header={TEXT?.CORPORATE_ADDRESS}>
                      <div className="flex flex-col gap-gap-md md:flex-row Qmd:flex-row items-start justify-between ">
                        <div className="flex flex-col gap-y-gap-xs">
                          {(() => {
                            const presentAddress =
                              dinVerificationData?.din_details?.din_number ===
                              dinDetailsData?.din_details?.din_number
                                ? dinVerificationData?.din_details
                                    ?.present_address ||
                                  dinDetailsData?.din_details?.present_address
                                : dinDetailsData?.din_details?.present_address;

                            return (
                              <p className="text-text-primary text-font-sm font-normal">
                                {presentAddress || "N/A"}
                              </p>
                            );
                          })()}
                        </div>

                        {!loading ? (
                          <div className="flex justify-center Qmd:justify-center md:justify-end Qmd:max-w-max-map-leaflet-width w-width-full Qmd:flex-shrink-0 flex-shrink">
                            <GeoMapLeaflet
                              address={(() => {
                                const address =
                                  dinVerificationData?.din_details
                                    ?.din_number ===
                                  dinDetailsData?.din_details?.din_number
                                    ? dinVerificationData?.din_details
                                        ?.present_address ||
                                      dinDetailsData?.din_details
                                        ?.present_address
                                    : dinDetailsData?.din_details
                                        ?.present_address;

                                return address || "";
                              })()}
                              owner="cin_details"
                              enableGeocoding={false}
                            />
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                    </CommonContainerCard>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (!dinDetailsLoading && dinDetailsError === true) ||
        dinStatus === "error" ? (
        <div className="flex flex-col justify-center items-center mx-auto my-auto">
          <img
            src={ICONS?.NO_DATA_FOUND}
            alt="notfound"
            className="px-padding-md w-graphic-width-md h-graphic-rectangle-height-md"
          />
          <p className="text-font-sm text-text-primary text-center font-sans font-normal py-padding-sm">
            No data found.
          </p>
          {!popup && (
            <PrimaryButton
              btnText="Clear parameters"
              enabledBgColor="var(--primary-act)"
              enabledTextColor="var(--text-primary)"
              handleFunction={() => {
                dispatch(resetGetDinData());
                setDinNo("");
              }}
              hoverOption="white"
              isDisabled={false}
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col justify-center items-center mx-auto my-auto">
          <img
            src={ICONS?.IFSC_VERIFICATION_GRAPHICS}
            alt="bgDefault"
            className="px-padding-md h-graphic-square-height-md w-graphic-width-md"
          />
          <p className="text-font-md text-text-primary font-normal text-center py-padding-sm">
            Enter DIN number in the search bar, to fetch the verified
            <br></br>corporate profile details.
          </p>
        </div>
      )}
    </div>
  );
};

export default DINVerification;
